import React, { useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faSort, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import LoadingTableSkeleton from "../Elements/TableLoader";
import SynchronizedAreaChart2 from "../Elements/Chart2";
import { ChartPoint, HashPoint, minerApiPath, toChartData } from "./publicMinerApi";

// A worker on the public miner dashboard; mirrors the logged-in Workers table without source filters or
// source badges, since customers of this page don't know other kinds of accounts exist.
export interface PublicWorker {
    workerId: string;
    hashRate15: number | null;
    hashRate24: number | null;
    status: string;
}

type StatusFilter = 'all' | 'online' | 'offline' | 'dead';
type SortKey = 'workerId' | 'hashRate15' | 'hashRate24';

interface PublicMinerWorkersProps {
    address: string;
    workers: PublicWorker[];
    loading: boolean;
}

const STATUS_FILTERS: StatusFilter[] = ['all', 'online', 'offline', 'dead'];

const cleanDigit = (n: number | null | undefined): string => (n == null ? "0.00" : Number(n).toFixed(2));

const StatusDot: React.FC<{ online: boolean; size?: string }> = ({ online, size = "w-2 h-2" }) => (
    <div className="relative">
        <div className={`${size} rounded-full ${online ? "bg-emerald-500" : "bg-rose-500"} relative z-10`} />
        {online && <div className={`absolute inset-0 ${size} rounded-full bg-emerald-500 animate-ping opacity-60`} />}
    </div>
);

const PublicMinerWorkers: React.FC<PublicMinerWorkersProps> = ({ address, workers, loading }) => {
    const [filter, setFilter] = useState<StatusFilter>('all');
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState<{ key: SortKey | null; direction: "asc" | "desc" }>({ key: null, direction: "asc" });
    const [openWorker, setOpenWorker] = useState<string | null>(null);
    const [chartData, setChartData] = useState<ChartPoint[]>([]);

    const counts = useMemo(() => {
        const next = { all: 0, online: 0, offline: 0, dead: 0 };
        workers.forEach(worker => {
            switch (worker.status?.toLowerCase()) {
                case 'dead':
                    next.dead++;
                    break;
                case 'online':
                    next.online++;
                    next.all++;
                    break;
                case 'offline':
                    next.offline++;
                    next.all++;
                    break;
            }
        });
        return next;
    }, [workers]);

    const rows = useMemo(() => {
        const term = searchTerm.toLowerCase();
        const filtered = workers.filter(worker => {
            const status = worker.status?.toLowerCase();
            const statusMatches = filter === 'all' ? status !== 'dead' : status === filter;
            return statusMatches && worker.workerId.toLowerCase().includes(term);
        });
        const { key, direction } = sortConfig;
        if (!key) {
            return filtered;
        }
        const sign = direction === "asc" ? 1 : -1;
        return [...filtered].sort((a, b) => {
            if (key === 'workerId') {
                return sign * a.workerId.toLowerCase().localeCompare(b.workerId.toLowerCase());
            }
            return sign * ((Number(a[key]) || 0) - (Number(b[key]) || 0));
        });
    }, [workers, filter, searchTerm, sortConfig]);

    const handleSort = (key: SortKey) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
        }));
    };

    const renderSortIcon = (key: SortKey) => {
        if (sortConfig.key !== key) return <FontAwesomeIcon icon={faSort} />;
        return <FontAwesomeIcon icon={sortConfig.direction === "asc" ? faSortUp : faSortDown} />;
    };

    const toggleWorker = (workerId: string) => {
        if (openWorker === workerId) {
            setOpenWorker(null);
            return;
        }
        setOpenWorker(workerId);
        setChartData([]);
        axios.get<HashPoint[]>(`${minerApiPath(address)}/workers/${encodeURIComponent(workerId)}/chart`)
            .then(response => setChartData(toChartData(response.data)))
            .catch(error => {
                console.error('Error fetching worker chart:', error);
                setChartData([]);
            });
    };

    return (
        <div className="flex flex-col p-5">
            <div className="md:flex md:justify-between md:items-center">
                <div className="flex space-x-2 p-2 overflow-x-auto pb-3">
                    {STATUS_FILTERS.map(status => (
                        <button
                            key={status}
                            className={`flex-shrink-0 text-xs py-2.5 rounded-xl flex items-center justify-between px-4 transition-all duration-300 font-bold tracking-tight ${filter === status
                                ? 'bg-[#0E4D92] text-white shadow-md'
                                : 'bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-[#0E4D92] dark:hover:text-blue-300 hover:bg-blue-50/50 dark:hover:bg-slate-700/50'
                                }`}
                            disabled={filter === status}
                            onClick={() => setFilter(status)}
                        >
                            <div className="flex items-center">
                                {(status === 'online' || status === 'offline') && (
                                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                                )}
                                {status === 'dead' && <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-gray-500" />}
                                <span className="font-medium text-[10px] sm:text-xs">
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </span>
                            </div>
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold min-w-6 text-center ml-1.5 ${filter === status
                                ? 'bg-white/30 text-white'
                                : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300'
                                }`}>
                                {counts[status]}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="w-full md:w-auto p-2 mt-2 md:mt-0">
                    <input
                        type="text"
                        placeholder="Search by Worker..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bg-white dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 px-5 py-2.5 text-sm rounded-xl w-full md:w-64 focus:border-[#0E4D92] dark:focus:border-blue-500 outline-none transition-all text-slate-700 dark:text-slate-100 placeholder-slate-400 shadow-sm"
                    />
                </div>
            </div>

            {loading ? (
                <LoadingTableSkeleton />
            ) : (
                <>
                    <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm transition-all">
                        <table className="w-full text-left text-slate-600 dark:text-slate-400">
                            <thead className="text-[#0E4D92] dark:text-blue-400 uppercase tracking-[0.1em] text-sm bg-slate-50/50 dark:bg-slate-800/60 backdrop-blur-sm border-b border-gray-100 dark:border-slate-800">
                                <tr>
                                    <th scope="col" className="px-4 py-3 font-bold cursor-pointer hover:text-[#0880D4] transition-colors" onClick={() => handleSort("workerId")}>
                                        <div className="flex items-center gap-1">Worker {renderSortIcon("workerId")}</div>
                                    </th>
                                    <th scope="col" className="px-4 py-3 font-bold text-center cursor-pointer hover:text-[#0880D4] transition-colors" onClick={() => handleSort("hashRate15")}>
                                        <div className="flex items-center justify-center gap-1">15m Hashrate {renderSortIcon("hashRate15")}</div>
                                    </th>
                                    <th scope="col" className="px-4 py-3 font-bold text-center cursor-pointer hover:text-[#0880D4] transition-colors" onClick={() => handleSort("hashRate24")}>
                                        <div className="flex items-center justify-center gap-1">24hr Hashrate {renderSortIcon("hashRate24")}</div>
                                    </th>
                                    <th scope="col" className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="text-center font-medium text-slate-400 py-6 text-sm">No active workers found</td>
                                    </tr>
                                ) : (
                                    rows.map(worker => (
                                        <React.Fragment key={worker.workerId}>
                                            <tr
                                                className="bg-white dark:bg-slate-800/50 border-b border-blue-50/50 dark:border-slate-700/30 hover:bg-blue-50/20 dark:hover:bg-slate-700/50 transition-all cursor-pointer group"
                                                onClick={() => toggleWorker(worker.workerId)}
                                            >
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center font-medium text-[#03436f] dark:text-white">
                                                        <div className="mr-2"><StatusDot online={worker.status === "ONLINE"} /></div>
                                                        <span className="text-sm break-all">{worker.workerId}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className="text-sm font-medium text-[#03436f] dark:text-blue-400">{cleanDigit(worker.hashRate15)}</span>
                                                    <span className="text-[8px] ml-1 font-medium opacity-40">TH/S</span>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className="text-sm font-medium text-[#03436f] dark:text-blue-400">{cleanDigit(worker.hashRate24)}</span>
                                                    <span className="text-[8px] ml-1 font-medium opacity-40">TH/S</span>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className={`inline-flex items-center justify-center w-7 h-7 rounded-lg bg-slate-50 dark:bg-slate-700 group-hover:bg-[#0880D4] group-hover:text-white transition-all duration-300 ${openWorker === worker.workerId ? "rotate-180 bg-[#0880D4] text-white" : "text-slate-400"}`}>
                                                        <FontAwesomeIcon icon={faChevronDown} className="text-[10px]" />
                                                    </div>
                                                </td>
                                            </tr>
                                            {openWorker === worker.workerId && (
                                                <tr className="bg-blue-50/20 dark:bg-slate-900/40">
                                                    <td colSpan={4} className="p-0 border-b border-blue-100 dark:border-slate-700">
                                                        <div className="p-4">
                                                            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl p-4 rounded-xl border border-white dark:border-slate-700 shadow-md">
                                                                <h3 className="text-lg font-bold text-[#03436f] dark:text-white">Worker Details</h3>
                                                                <p className="text-xs font-semibold text-[#0880D4] uppercase tracking-widest mt-1 mb-4">24hr Hashrate Analysis</p>
                                                                <SynchronizedAreaChart2 data={chartData} />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="md:hidden space-y-3">
                        {rows.length === 0 && (
                            <p className="text-center font-medium text-slate-400 py-6 text-sm">No active workers found</p>
                        )}
                        {rows.map(worker => (
                            <div
                                key={worker.workerId}
                                className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-xl p-4 border border-blue-50 dark:border-slate-700/50 shadow-lg transition-all"
                                onClick={() => toggleWorker(worker.workerId)}
                            >
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <StatusDot online={worker.status === "ONLINE"} size="w-2.5 h-2.5" />
                                        <span className="font-bold text-[#03436F] dark:text-white text-sm break-all">{worker.workerId}</span>
                                    </div>
                                    <div className={`w-7 h-7 flex-shrink-0 rounded-lg bg-slate-50 dark:bg-slate-700 flex items-center justify-center transition-all ${openWorker === worker.workerId ? "rotate-180 bg-[#0880D4] text-white" : "text-slate-400"}`}>
                                        <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-50 dark:border-slate-700/50">
                                    <div>
                                        <p className="text-[9px] font-semibold text-blue-400 dark:text-slate-500 uppercase tracking-widest mb-1">15m Hash</p>
                                        <p className="text-lg font-bold text-[#03436f] dark:text-white">{cleanDigit(worker.hashRate15)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-semibold text-blue-400 dark:text-slate-500 uppercase tracking-widest mb-1">24h Hash</p>
                                        <p className="text-lg font-bold text-[#03436f] dark:text-white">{cleanDigit(worker.hashRate24)}</p>
                                    </div>
                                </div>
                                {openWorker === worker.workerId && (
                                    <div className="mt-6 pt-4 border-t border-slate-50 dark:border-slate-700/50">
                                        <div className="bg-slate-50/50 dark:bg-slate-900/50 rounded-xl p-3">
                                            <SynchronizedAreaChart2 data={chartData} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default PublicMinerWorkers;
